<template>
  <div class="season-switcher">
    <!-- 主容器 -->
    <div class="switcher-container">
      <!-- 季节按钮 -->
      <button 
        v-for="(seasonItem, index) in seasons" 
        :key="index"
        @click="changeSeason(seasonItem.value)"
        class="season-button"
        :class="{ active: season === seasonItem.value }"
        :aria-label="seasonItem.label"
        :aria-pressed="season === seasonItem.value"
      >
        <!-- 背景色 -->
        <div class="season-bg"></div>
        
        <!-- 季节文字 -->
        <span class="season-text">{{ seasonItem.text }}</span>
        
        <!-- 季节图标 -->
        <div v-if="season === seasonItem.value" class="season-icon">
          <component :is="seasonItem.icon" class="icon" />
        </div>

        <!-- <div class="season-switcher-container">
          <SeasonSwitcher />
        </div> -->
        
        <!-- 活动指示器 -->
        <span v-if="season === seasonItem.value" class="active-indicator"></span>
      </button>
    </div>
    
    <!-- 季节名称提示 -->
    <div class="season-tooltip" :class="{ show: showTooltip }">
      {{ seasons.find(s => s.value === season)?.label }}
    </div>
    
    <!-- 装饰元素 -->
    <div class="decorative-dot"></div>
  </div>
</template>

<script setup>
import { inject, ref, onMounted, onUnmounted } from 'vue'
import { 
  Flower as FlowerIcon, 
  Sun as SunIcon, 
  Leaf as LeafIcon, 
  Snowflake as SnowflakeIcon 
} from 'lucide-vue-next'
// import { mapActions } from 'vuex'

const season = inject('currentSeason')
const showTooltip = ref(false)
let tooltipTimer = null

// 季节数据
const seasons = [
  { value: 'spring', text: '春', label: '春季', color: '#81C784', icon: FlowerIcon },
  { value: 'summer', text: '夏', label: '夏季', color: '#4FC3F7', icon: SunIcon },
  { value: 'autumn', text: '秋', label: '秋季', color: '#FF8A65', icon: LeafIcon },
  { value: 'winter', text: '冬', label: '冬季', color: '#90A4AE', icon: SnowflakeIcon }
]

// 切换季节
const changeSeason = (newSeason) => {
  if (season.value !== newSeason) {
    season.value = newSeason
    showTooltip.value = true
    clearTimeout(tooltipTimer)
    tooltipTimer = setTimeout(() => showTooltip.value = false, 2000)
  }
}

onMounted(() => {
  showTooltip.value = true
  tooltipTimer = setTimeout(() => showTooltip.value = false, 2000)
})

onUnmounted(() => clearTimeout(tooltipTimer))


// mapActions(['sendMessage']),
//     sendMessage(() => {
//       this.sendMessage('这是来自 ComponentA 的信息')
//     }
//   )
</script>

<style scoped>
/* 基础布局 */
.season-switcher {
  margin-right: -200px;
  position: relative;
}

.switcher-container {
  display: flex;
  gap: 4px;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(4px);
  border-radius: 999px;
  padding: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(139, 69, 19, 0.1);
  transition: all 0.3s ease;
}

.switcher-container:hover {
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15);
}

/* 季节按钮 */
.season-button {
  position: relative;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
}

.season-button:hover {
  transform: translateY(-1px);
}

.season-button:active {
  transform: translateY(0);
}

/* 按钮背景 */
.season-bg {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  opacity: 0;
  transform: scale(0);
  transition: all 0.3s ease;
}

.season-button.active .season-bg {
  opacity: 1;
  transform: scale(1);
}

/* 根据季节设置背景色 */
.season-button:nth-child(1) .season-bg { background: #81C784; }
.season-button:nth-child(2) .season-bg { background: #4FC3F7; }
.season-button:nth-child(3) .season-bg { background: #FF8A65; }
.season-button:nth-child(4) .season-bg { background: #90A4AE; }

/* 文字样式 */
.season-text {
  position: relative;
  z-index: 1;
  color: rgba(139, 69, 19, 0.7);
  font-size: 14px;
  transition: color 0.3s ease;
}

.season-button.active .season-text {
  color: white;
  font-weight: 500;
}

/* 季节图标 */
.season-icon {
  position: absolute;
  bottom: -2px;
  left: 50%;
  transform: translateX(-50%);
  opacity: 0;
  animation: fade-in 0.5s forwards;
}

.icon {
  width: 24px;
  height: 24px;
  color: rgba(255, 255, 255, 0.3);
}

/* 活动指示器 */
.active-indicator {
  position: absolute;
  bottom: -4px;
  left: 50%;
  transform: translateX(-50%);
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: white;
}

/* 季节指示器*/
.season-switcher-container {
  display: none;
}

/* 提示框 */
.season-tooltip {
  position: absolute;
  bottom: -32px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(4px);
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 12px;
  color: #8B4513;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  opacity: 0;
  transition: opacity 0.2s ease;
}

.season-tooltip.show {
  opacity: 1;
}

/* 装饰红点 */
.decorative-dot {
  position: absolute;
  top: -4px;
  right: -4px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: rgba(195, 39, 43, 0.8);
  opacity: 0.7;
}

/* 动画 */
@keyframes fade-in {
  from {
    opacity: 0;
    transform: translate(-50%, 100%);
  }
  to {
    opacity: 0.3;
    transform: translate(-50%, 0);
  }
}

@keyframes pulse {
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(139, 69, 19, 0.1);
  }
  50% {
    box-shadow: 0 0 0 4px rgba(139, 69, 19, 0.1);
  }
}

.season-button[aria-pressed="true"] {
  animation: pulse 2s infinite;
}
</style>